import { useState, useMemo } from "react";
import PageHeader from "@/components/PageHeader";
import CourseCard from "@/components/CourseCard";
import CTASection from "@/components/CTASection";
import { GOVT_CATEGORIES } from "@/constants/site";
import { motion } from "framer-motion";
import SEO, { breadcrumb } from "@/components/SEO";

const TAGS = ["All", "Civil Services", "MPSC", "SSC", "Banking", "Railway", "State", "Defence", "Teaching"];

export default function GovernmentCourses() {
  const [filter, setFilter] = useState("All");

  const filtered = useMemo(
    () => filter === "All" ? GOVT_CATEGORIES : GOVT_CATEGORIES.filter((c) => c.tag === filter),
    [filter]
  );

  return (
    <div>
      <SEO
        title="UPSC, MPSC, SSC, Banking, Railway & Defence Exam Coaching | AM Group of Education"
        description="Every Government Exam under one roof — UPSC, MPSC Rajyaseva, MPSC Combine, MPSC Group C, SSC, IBPS/SBI Banking, Railway, Police, Defence and Teaching exams."
        path="/government-courses"
        jsonLd={[
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Government Courses", path: "/government-courses" },
          ]),
        ]}
      />
      <PageHeader
        title="Government Courses"
        subtitle="Every Government Exam Under One Roof — expert faculty, structured study material, mock tests, interview guidance and mentorship."
        crumb="Government Courses"
      />

      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="kicker">India&apos;s Top Exams</span>
            <h2 className="display-serif mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark leading-tight">
              Every Government Exam <span className="italic text-brand-primary">Under One Roof</span>
            </h2>
            <p className="mt-4 text-brand-mute leading-relaxed">
              Prepare for India&apos;s top Government and Competitive Exams with expert faculty, structured study material, mock tests, interview guidance and mentorship.
            </p>
          </div>

          {/* Filter chips */}
          <div className="mt-8 flex flex-wrap gap-2" role="tablist" data-testid="govt-filter">
            {TAGS.map((t) => {
              const active = t === filter;
              return (
                <button
                  key={t}
                  onClick={() => setFilter(t)}
                  data-testid={`govt-filter-${t.toLowerCase().replace(/\s+/g,'-')}`}
                  className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-widest transition-all ${
                    active
                      ? "bg-brand-dark text-brand-gold border border-brand-gold shadow-[0_8px_25px_-10px_rgba(78,52,46,.4)]"
                      : "bg-brand-cream text-brand-primary border border-brand-line hover:bg-white"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>

          {/* Grid */}
          <motion.div
            layout
            className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6"
          >
            {filtered.map((c, i) => (
              <CourseCard
                key={c.title}
                title={c.title}
                tag={c.tag}
                desc={c.desc}
                icon={c.icon}
                delay={i * 0.03}
                testId={`govt-card-${c.title.toLowerCase().replace(/\s+/g,'-')}`}
              />
            ))}
          </motion.div>

          {filtered.length === 0 && (
            <div className="mt-14 text-center text-brand-mute">
              <i className="fa-solid fa-inbox text-3xl text-brand-line" />
              <p className="mt-3 text-sm">No exams matched that filter.</p>
            </div>
          )}
        </div>
      </section>

      <CTASection />
    </div>
  );
}
